/**
 * Format file size in bytes to human-readable string
 * @param bytes - File size in bytes
 * @param options - Formatting options
 * @returns Formatted string (e.g., "1.5 MB", "350 KB")
 */
export function formatFileSize(
  bytes: number | undefined | null,
  options?: { emptyValue?: string; decimals?: number }
): string {
  const { emptyValue = '0 Bytes', decimals = 1 } = options || {};

  if (bytes === undefined || bytes === null || bytes === 0) return emptyValue;

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}

/**
 * Sanitize a filename by replacing any character outside the printable-ASCII
 * range (0x20–0x7E) with an underscore. This prevents Azure Blob Storage HTTP
 * header encoding failures when filenames contain accented characters,
 * non-Latin scripts, OR ASCII control characters (NULL, tab, LF, CR, DEL) —
 * all of which break HTTP header parsing.
 *
 * The regex deliberately excludes control chars: the `no-control-regex` rule
 * therefore does not apply here (no eslint-disable needed).
 *
 * @param fileName - The original filename
 * @returns The sanitized filename with non-printable-ASCII characters replaced
 */
export function sanitizeFileName(fileName: string): string {
  if (!fileName) return fileName;
  return fileName.replace(/[^\x20-\x7E]/g, '_');
}

/**
 * Create a new File object with a sanitized (ASCII-only) filename.
 * Returns the original file if the name is already ASCII-safe.
 *
 * @param file - The original File object
 * @returns A new File with sanitized name, or the original if unchanged
 */
export function sanitizeFile(file: File): File {
  const sanitized = sanitizeFileName(file.name);
  if (sanitized === file.name) return file;
  return new File([file], sanitized, { type: file.type, lastModified: file.lastModified });
}

/**
 * Parse filename from Content-Disposition header
 * Supports both standard filename and RFC 5987 encoded filename* formats
 *
 * @example
 * // Standard format
 * parseContentDispositionFilename('attachment; filename="document.pdf"')
 * // Returns: "document.pdf"
 *
 * // RFC 5987 encoded format (for non-ASCII characters)
 * parseContentDispositionFilename('attachment; filename*=UTF-8\'\'Tax%20Return.zip')
 * // Returns: "Tax Return.zip"
 *
 * @param contentDisposition - The Content-Disposition header value
 * @returns The extracted filename, or null if not found
 */
export function parseContentDispositionFilename(contentDisposition: string | null | undefined): string | null {
  if (!contentDisposition) return null;

  // Try RFC 5987 encoded filename* first (preferred for non-ASCII)
  // Format: filename*=UTF-8''encoded_filename
  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      // Fall through to standard filename parsing
    }
  }

  // Try standard filename with quotes: filename="example.pdf"
  const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  // Try standard filename without quotes: filename=example.pdf
  const unquotedMatch = contentDisposition.match(/filename\s*=\s*([^;\s]+)/);
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1];
  }

  return null;
}
